import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, Loader2, Mail, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthModal({ open, onClose }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const close = () => {
    setMode("signin"); setSent(false); setError(""); setPassword("");
    onClose();
  };

  const goGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const finish = (data) => {
    if (data.token) localStorage.setItem("bf_session_token", data.token);
    setUser(data.user);
    toast.success(`Welcome, ${data.user?.name?.split(" ")[0] || "there"}!`);
    const pending = localStorage.getItem("bf_pending_invite");
    if (pending) { localStorage.removeItem("bf_pending_invite"); navigate(`/join/${pending}`, { replace: true }); return; }
    navigate("/dashboard", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setSent(true);
      } else {
        const path = mode === "signup" ? "/auth/register" : "/auth/login";
        const payload = mode === "signup" ? { email, password, name } : { email, password };
        const { data } = await api.post(path, payload);
        finish(data);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const switchMode = (m) => { setMode(m); setError(""); setSent(false); };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={close} data-testid="auth-modal">
      <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-card border border-border rounded-2xl p-6 shadow-2xl relative">
        <button onClick={close} className="absolute top-4 right-4 p-1 rounded-md hover:bg-accent text-muted-foreground" data-testid="auth-close"><X className="h-4 w-4" /></button>

        <div className="flex items-center gap-2.5 mb-1">
          <img src="/logo.png" alt="OWEverit" className="h-8 w-8 rounded-lg" />
          <div className="font-display font-extrabold text-lg">OWEverit</div>
        </div>

        {mode === "forgot" ? (
          <>
            <h2 className="font-display text-xl font-bold mt-3">Reset your password</h2>
            {sent ? (
              <div className="mt-4 p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-sm flex items-start gap-2" data-testid="forgot-sent">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                <div>If an account exists for <span className="font-semibold">{email}</span>, we've sent a reset link. Check your inbox (and spam).</div>
              </div>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mt-1">Enter your email and we'll send you a link to set a new password.</p>
                <form onSubmit={submit} className="mt-4 space-y-3">
                  <Field label="Email" type="email" value={email} onChange={setEmail} testid="auth-email" autoFocus />
                  {error && <p className="text-xs text-rose-500" data-testid="auth-error">{error}</p>}
                  <Submit busy={busy} testid="forgot-submit"><Mail className="h-4 w-4" /> Send reset link</Submit>
                </form>
              </>
            )}
            <button onClick={() => switchMode("signin")} className="mt-4 text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-signin">← Back to sign in</button>
          </>
        ) : (
          <>
            <div className="flex gap-1 mt-3 p-1 bg-accent rounded-xl border border-border">
              <button onClick={() => switchMode("signin")} data-testid="tab-signin"
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${mode === "signin" ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>Sign in</button>
              <button onClick={() => switchMode("signup")} data-testid="tab-signup"
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${mode === "signup" ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>Create account</button>
            </div>

            <button onClick={goGoogle} data-testid="auth-google-btn"
              className="mt-4 w-full flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-lg bg-white text-slate-900 font-semibold hover:bg-slate-100 transition">
              <GoogleIcon /> Continue with Google
            </button>
            <div className="flex items-center gap-3 my-4"><span className="h-px flex-1 bg-border" /><span className="text-[10px] uppercase tracking-wider text-muted-foreground">or</span><span className="h-px flex-1 bg-border" /></div>

            <form onSubmit={submit} className="space-y-3">
              {mode === "signup" && <Field label="Name" value={name} onChange={setName} testid="auth-name" placeholder="Your name" />}
              <Field label="Email" type="email" value={email} onChange={setEmail} testid="auth-email" />
              <Field label="Password" type="password" value={password} onChange={setPassword} testid="auth-password"
                hint={mode === "signup" ? "At least 8 characters, with a letter and a number" : undefined} />
              {mode === "signin" && (
                <button type="button" onClick={() => switchMode("forgot")} data-testid="forgot-link" className="text-xs text-teal-500 hover:underline">Forgot password?</button>
              )}
              {error && <p className="text-xs text-rose-500" data-testid="auth-error">{error}</p>}
              <Submit busy={busy} testid="auth-submit">
                {mode === "signup" ? "Create account" : "Sign in"} <ArrowRight className="h-4 w-4" />
              </Submit>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, hint, autoFocus, placeholder }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} required autoFocus={autoFocus} placeholder={placeholder}
        data-testid={testid}
        className="mt-1 w-full px-3 py-2.5 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40" />
      {hint && <span className="text-[10px] text-muted-foreground mt-1 block">{hint}</span>}
    </label>
  );
}

function Submit({ busy, children, testid }) {
  return (
    <button type="submit" disabled={busy} data-testid={testid}
      className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-teal-500 text-white text-sm font-semibold hover:bg-teal-600 transition disabled:opacity-50">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : children}
    </button>
  );
}

function GoogleIcon() {
  return <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.8-5.5 3.8-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.9 1.5l2.6-2.6C16.9 3.1 14.7 2 12 2 6.5 2 2 6.5 2 12s4.5 10 10 10c5.8 0 9.6-4.1 9.6-9.8 0-.7-.1-1.2-.2-1.7H12z" /></svg>;
}
